import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { Coins, Gamepad2, Gem } from 'lucide-react';
import type { PyramidaUiState } from '../hooks/usePyramidaState';
import { useVaultData } from '../hooks/useVaultData';
import {
  CONTRACT_PARAMS,
  IS_CONTRACT_CONFIGURED,
  LIQUIDITY_ENABLED,
  SWAP_ENABLED,
} from '../ton/config';
import { fmtUnits9 } from '../ton/vault';
import { TokenMarketCard } from './TokenMarketCard';
import { RewardsCard } from './RewardsCard';
import { VaultLiquidityCard } from './VaultLiquidityCard';
import { formatPrmd } from '../lib/format';

const DefiCards = lazy(() => import('./DefiCards'));

interface JettonInfoProps {
  state: PyramidaUiState;
  onPlay?: () => void;
}

/**
 * Assets — the wallet hub of the ecosystem: portfolio at a glance, claimable
 * rewards, mini-games bank liquidity (add/remove), PRMD swap and pool, token
 * market info. Every block funnels back into playing.
 */
export function JettonInfo({ state, onPlay }: JettonInfoProps) {
  const { t } = useTranslation();
  const { token, me } = state;
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const vaultData = useVaultData(address || null);

  const minted = token.totalMinted;
  const max = token.maxSupply;
  const pct = max > 0 ? Math.min(100, (minted / max) * 100) : 0;
  const remaining = Math.max(0, max - minted);

  const firstLoad = IS_CONTRACT_CONFIGURED && state.lastUpdated === null;

  return (
    <section className="content-section">
      <header className="page-head">
        <h1 className="lottery-title">{t('v2.nav.assets')}</h1>
        <span className="page-head__sub">{t('v2.assets.subtitle')}</span>
      </header>

      <div className="page-grid">
        <div className="page-grid__main">
          {/* portfolio at a glance */}
          <div className="token-info-card portfolio-card">
            <h3 className="card-head">
              <Coins size={18} aria-hidden="true" /> {t('v2.assets.portfolio')}
            </h3>
            {address ? (
              <div className="portfolio-card__grid">
                <div>
                  <span>GRAM</span>
                  <strong>{fmtUnits9(vaultData.gramBalance)}</strong>
                </div>
                <div>
                  <span>PRMD</span>
                  <strong>
                    {firstLoad ? (
                      <span className="skeleton skeleton--line" />
                    ) : (
                      formatPrmd(me?.prmdBalance ?? 0)
                    )}
                  </strong>
                </div>
                <div>
                  <span>{t('v2.assets.lpShort')}</span>
                  <strong>{fmtUnits9(vaultData.lpValue)} GRAM</strong>
                </div>
                <div>
                  <span>{t('v2.assets.rewardsShort')}</span>
                  <strong>{fmtUnits9(vaultData.refBalance, 4)} GRAM</strong>
                </div>
              </div>
            ) : (
              <>
                <p className="hint" style={{ textAlign: 'left' }}>
                  {t('v2.assets.connectHint')}
                </p>
                <button
                  type="button"
                  className="swap-submit"
                  onClick={() => void tonConnectUI.openModal()}
                >
                  {t('v2.vault.connect')}
                </button>
              </>
            )}
          </div>

          <VaultLiquidityCard data={vaultData} />

          {(LIQUIDITY_ENABLED || SWAP_ENABLED) && (
            <Suspense fallback={null}>
              <DefiCards />
            </Suspense>
          )}

          <div className="token-info-card">
            <h3 className="card-head">
              <Gem size={18} aria-hidden="true" /> Pyramida (PRMD)
            </h3>
            <p className="hint" style={{ textAlign: 'left' }}>
              {t('jetton.maxEmission', { max: formatPrmd(CONTRACT_PARAMS.maxSupply) })}
            </p>

            <div className="supply-bar" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
              <div className="supply-bar__fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="supply-bar__legend">
              <span>{t('jetton.minted', { amount: formatPrmd(minted) })}</span>
              <span>{pct.toFixed(1)}%</span>
              <span>{t('jetton.max', { amount: formatPrmd(max) })}</span>
            </div>
          </div>
        </div>

        <div className="page-grid__side">
          <RewardsCard data={vaultData} registered={me?.registered ?? false} />

          <TokenMarketCard
            remaining={remaining}
            mintable={token.mintable}
            lastMintTime={token.lastMintTime}
            jettonMasterAddress={state.system.jettonMasterAddress}
            firstLoad={firstLoad}
          />

          {onPlay && (
            <div className="token-info-card play-nudge">
              <h3 className="card-head">
                <Gamepad2 size={18} aria-hidden="true" /> {t('v2.assets.playTitle')}
              </h3>
              <p className="hint" style={{ textAlign: 'left' }}>
                {t('v2.assets.playPitch')}
              </p>
              <button type="button" className="swap-submit" onClick={onPlay}>
                {t('v2.assets.playCta')}
              </button>
            </div>
          )}

          {!state.isLive && (
            <p className="hint">{t('jetton.demoHint')}</p>
          )}
        </div>
      </div>
    </section>
  );
}
